import React, { useEffect, useRef } from "react";

export default function Hero() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.35;
        this.speedY = (Math.random() - 0.5) * 0.35;
        this.opacity = Math.random() * 0.35 + 0.08;
        this.color = Math.random() > 0.5 ? "#1d4ed8" : "#3b82f6";
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    for (let i = 0; i < 90; i++) particles.push(new Particle());

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = "#3b82f6";
            ctx.globalAlpha = (1 - dist / 110) * 0.1;
            ctx.lineWidth = 0.6;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
      }
      particles.forEach((p) => { p.update(); p.draw(); });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen mt-16 flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100"
    >
      {/* Animated canvas bg */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Soft radial glow blobs */}
      <div className="absolute top-20 left-1/4 w-72 h-72 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-5 text-center">

        {/* Badge */}
       <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-300 rounded-full px-5 py-2 mb-4 mt-4 shadow-sm animate-fade-in">
  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-sky-400 shadow-md shadow-blue-300" />
  <span
    className="text-xs font-bold tracking-widest uppercase"
    style={{
      background: "linear-gradient(135deg,#1d4ed8,#0ea5e9)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    }}
  >
    Complete Digital Solutions
  </span>
  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-sky-400 to-blue-500 shadow-md shadow-blue-300" />
</div>

        {/* Headline */}
        <h1
          className="font-black text-5xl md:text-7xl lg:text-8xl leading-[1.05] tracking-tight mb-6 animate-fade-up text-black"
          style={{ fontFamily: "'Playfair Display', serif" }}
        > 
         Helping{" "}
          <span
            className="relative inline-block"
            style={{
              fontFamily: "'Playfair Display', serif",
              background: "linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Businesses
          </span>
          <br />
          Grow With{" "}
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              background: "linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Powerful Digital Solutions
          </span>
        </h1>

        {/* Service pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-5 animate-fade-up" style={{ animationDelay: "0.2s" }}>
          {["Websites", "Apps", "Video Editing", "Branding", "Meta Ads"].map((item) => (
            <span
              key={item}
              className="bg-white/80 border border-blue-200 text-blue-800 text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              {item}
            </span>
          ))}
        </div>

        {/* Trust line */}
        <p
          className="text-slate-700 text-sm md:text-base mb-10 animate-fade-up"
          style={{ fontFamily: "'Nunito', sans-serif", animationDelay: "0.3s" }}
        >
          Trusted by businesses across Haryana, India &amp; globally — fast, creative, result-driven.
        </p>

        {/* Buttons */}
        <div
          className="flex flex-wrap items-center justify-center gap-4 animate-fade-up"
          style={{ animationDelay: "0.4s" }}
        >
          {/* Primary */}
          <a
            href="#portfolio"
            className="group relative overflow-hidden bg-blue-600 text-white font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-300/50 hover:bg-blue-700"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            <span className="relative z-10 flex items-center gap-2">
              View Portfolio
              <span className="group-hover:translate-x-1 inline-block transition-transform duration-200">→</span>
            </span>
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.me/9253172295"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 bg-green-50 border border-green-300 text-green-700 font-semibold text-sm px-7 py-4 rounded-full hover:bg-green-100 transition-all duration-300 hover:scale-105 shadow-sm"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.555 4.126 1.526 5.859L0 24l6.335-1.507A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.667-.5-5.207-1.375l-.375-.219-3.758.894.952-3.664-.244-.389A9.93 9.93 0 012 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"/>
            </svg>
            Chat on WhatsApp
          </a>

          {/* Ghost */}
          <a
            href="#contact"
            className="bg-white/70 backdrop-blur-sm border border-blue-200 text-blue-800 font-medium text-sm px-7 py-4 rounded-full hover:bg-white hover:border-blue-400 hover:shadow-md transition-all duration-300 hover:scale-105"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Book Free Consultation
          </a>
        </div>

      
      </div>

      {/* Google Fonts import for new fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Nunito:wght@400;500;600;700;800&display=swap');
      `}</style>
    </section>
  );
}